import { Button } from "@/components/design-system/button";
import CourseBuilderForm from "@/modules/Course-Management/components/course-builder/CourseBuilderForm";
import {
  courseViewToBuilderModel,
  mergeBuilderPublishState,
  needsListPublishMetaLookup,
  resolveCoursePublishMeta,
} from "@/service/course.mappers";
import {
  getCourseListItemByIdService,
  getCourseService,
  updateCourseService,
} from "@/service/course.service";
import type {
  CourseBuilderModel,
  UpdateCoursePayload,
} from "@/types/course.model";
import { successToast } from "@/utils/constants";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

type EditCourseLocationState = {
  is_published?: boolean;
  is_active?: boolean;
};

export default function EditCourse() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [initial, setInitial] = useState<CourseBuilderModel | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setLoadError("Invalid course id.");
      return;
    }

    const listState = (location.state ?? {}) as EditCourseLocationState;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setInitial(null);

    (async () => {
      try {
        const viewRes = await getCourseService(id, null);
        if (cancelled) return;

        const view = viewRes.data?.data;
        if (!view) {
          setLoadError("Could not load this course for editing.");
          return;
        }

        const hasContent =
          (view.terms?.length ?? 0) > 0 || (view.lessons?.length ?? 0) > 0;
        if (!hasContent) {
          setLoadError("This course has no syllabus content to edit.");
          return;
        }

        let listRow: Awaited<ReturnType<typeof getCourseListItemByIdService>> =
          null;
        if (needsListPublishMetaLookup(listState, view)) {
          listRow = await getCourseListItemByIdService(id).catch(() => null);
          if (cancelled) return;
        }

        const builder = courseViewToBuilderModel(view);
        const meta = resolveCoursePublishMeta(listState, listRow, view);
        setInitial(mergeBuilderPublishState(builder, meta));
      } catch (e: unknown) {
        console.error(e);
        if (!cancelled) {
          setLoadError("Could not load this course for editing.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, location.state]);

  const handleUpdate = async (payload: UpdateCoursePayload) => {
    if (!id) return;
    try {
      await updateCourseService(id, payload);
      if (payload.is_published) {
        successToast(
          initial?.is_published === false ? "Course published" : "Course updated",
          initial?.is_published === false
            ? "The course was published successfully."
            : "Your changes were saved successfully."
        );
      } else {
        successToast("Draft saved", "The course was saved as draft.");
      }
      navigate("/course-management");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-[68px] px-[32px] items-center flex justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[40px] h-[40px] p-0 bg-white border border-[#EDEDED] rounded-[8px]"
            onClick={() => navigate(-1)}
            leftIcon={<ChevronLeft className="w-5 h-5" />}
          >
            <span className="sr-only">Back</span>
          </Button>
          <div className="text-[#1F1F1F] text-[16px] font-semibold">
            Edit Course
          </div>
        </div>
      </div>

      <div className="flex-1 px-[32px] pb-[24px]">
        {loading && (
          <p className="text-[#1F1F1F] text-[16px] font-medium py-8">
            Loading course…
          </p>
        )}
        {!loading && loadError && (
          <p className="text-[#ef4444] text-[16px] font-medium py-8">
            {loadError}
          </p>
        )}
        {!loading && !loadError && initial && id && (
          <CourseBuilderForm
            key={`${id}-${initial.is_published}`}
            mode="edit"
            initialValue={initial}
            onSubmit={handleUpdate}
            onClose={() => navigate(-1)}
          />
        )}
      </div>
    </div>
  );
}
